import { useTranslation } from 'react-i18next'
import Card from '@/components/ui/Card'
import Input from '@/components/ui/Input'
import Button from '@/components/ui/Button'
import type { ZakatCropsInput, CalendarType, IrrigationType } from '@/types/zakat'
import { Sprout } from 'lucide-react'

interface ZakatCropsFormProps {
  input: ZakatCropsInput
  calendar: CalendarType
  onInputChange: (input: ZakatCropsInput) => void
  onCalendarChange: (calendar: CalendarType) => void
  onCalculate: () => void
}

export default function ZakatCropsForm({
  input,
  calendar,
  onInputChange,
  onCalendarChange,
  onCalculate,
}: ZakatCropsFormProps) {
  const { t } = useTranslation()
  const handleInputChange = (field: keyof ZakatCropsInput, value: string | IrrigationType) => {
    if (field === 'irrigationType') {
      onInputChange({
        ...input,
        irrigationType: value as IrrigationType,
      })
    } else {
      const numValue = parseFloat(value as string) || 0
      onInputChange({
        ...input,
        [field]: numValue,
      })
    }
  }

  return (
    <Card>
      <div className="flex items-center gap-3 mb-6">
        <Sprout className="w-6 h-6 text-primary-600 dark:text-primary-400" />
        <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('zakat.crops.title')}</h3>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          {t('zakat.yearOfHawl')}
        </label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="calendar"
              value="hijri"
              checked={calendar === 'hijri'}
              onChange={() => onCalendarChange('hijri')}
              className="w-4 h-4 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-gray-700 dark:text-gray-300">{t('zakat.hijri')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="calendar"
              value="gregorian"
              checked={calendar === 'gregorian'}
              onChange={() => onCalendarChange('gregorian')}
              className="w-4 h-4 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-gray-700 dark:text-gray-300">{t('zakat.gregorian')}</span>
          </label>
        </div>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          {t('zakat.crops.irrigationType')}
        </label>
        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="irrigation"
              value="withCost"
              checked={input.irrigationType === 'withCost'}
              onChange={() => handleInputChange('irrigationType', 'withCost')}
              className="w-4 h-4 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-gray-700 dark:text-gray-300">{t('zakat.crops.withCost')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="irrigation"
              value="withoutCost"
              checked={input.irrigationType === 'withoutCost'}
              onChange={() => handleInputChange('irrigationType', 'withoutCost')}
              className="w-4 h-4 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-gray-700 dark:text-gray-300">{t('zakat.crops.withoutCost')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="irrigation"
              value="sharedCost"
              checked={input.irrigationType === 'sharedCost'}
              onChange={() => handleInputChange('irrigationType', 'sharedCost')}
              className="w-4 h-4 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-gray-700 dark:text-gray-300">{t('zakat.crops.sharedCost')}</span>
          </label>
        </div>
      </div>

      <div className="space-y-4 mb-6">
        <Input
          type="number"
          label={t('zakat.crops.kilograms')}
          value={input.kilograms.toString()}
          onChange={(e) => handleInputChange('kilograms', e.target.value)}
          placeholder={t('zakat.crops.placeholderKilograms')}
        />
        <Input
          type="number"
          label={t('zakat.crops.pricePerKilogram')}
          value={input.pricePerKilogram.toString()}
          onChange={(e) => handleInputChange('pricePerKilogram', e.target.value)}
          placeholder={t('zakat.crops.placeholderPrice')}
        />
      </div>

      <Button variant="primary" size="lg" className="w-full" onClick={onCalculate}>
        {t('zakat.calculateYourZakat')}
      </Button>
    </Card>
  )
}


